import { PDFDocument, PDFFont, PDFPage, rgb } from 'pdf-lib';
import * as fontkit from 'fontkit'; // Import FontKit
import { Fontkit } from 'pdf-lib/cjs/types/fontkit';
import { Order } from '../components/pages/generate-sales/types';
import dayjs from 'dayjs';
import { Inventory, Product } from '../components/pages/inventory/types';
import { formatCurrency } from './auth';

function truncateStringSafe(str: string, maxLength: number): string {
  if (maxLength <= 3) {
    return str.length > 0 ? '...' : '';
  }
  return str.length <= maxLength ? str : str.slice(0, maxLength - 3) + '...';
}

const drawPaymentType = (
  firstPage: PDFPage,
  customFont: PDFFont,
  order: Order,
) => {
  const { height } = firstPage.getSize();
  const text: string = '✓';
  firstPage.drawText(text, {
    x: 52,
    y: height - (order?.payment_type === 'cash' ? 129 : 145),
    size: 14,
    font: customFont,
    color: rgb(0, 0, 0),
  });
};

const drawInvoiceNumber = (
  firstPage: PDFPage,
  customFont: PDFFont,
  order: Order,
) => {
  const { height } = firstPage.getSize();
  const text: string = `${order?.invoice_number || ''}`;
  firstPage.drawText(text, {
    x: 410,
    y: height - 105,
    size: 10,
    font: customFont,
    color: rgb(0, 0, 0),
  });
};

const drawDate = (firstPage: PDFPage, customFont: PDFFont, order: Order) => {
  const { height } = firstPage.getSize();
  const text: string = `${dayjs(order?.transaction_date || '').format('MMMM D, YYYY')}`;
  firstPage.drawText(text, {
    x: 400,
    y: height - 137,
    size: 10,
    font: customFont,
    color: rgb(0, 0, 0),
  });
};

const drawNameTinAddress = (
  firstPage: PDFPage,
  customFont: PDFFont,
  order: Order,
) => {
  const { height } = firstPage.getSize();
  const { customer_id, tin, billing_address } = order;
  // eslint-disable-next-line
  const co_doctor_name = (customer_id as any)?.codoctors?.co_doctor_name
  const texts: string[] = [
    // eslint-disable-next-line
    (customer_id as any)?.customer_name,
    tin || '',
    (co_doctor_name ? `${co_doctor_name} - ` : '') + (billing_address || ''),
  ]; 

  texts.forEach((text, key) =>
    firstPage.drawText(text, {
      x: 145,
      y: height - (187 + 23 * key),
      size: 10,
      font: customFont,
      color: rgb(0, 0, 0),
    }),
  );
};

const drawOrderItems = (
  firstPage: PDFPage,
  customFont: PDFFont,
  order: Order,
) => {
  const { width, height } = firstPage.getSize();
  const { order_items } = order;

  (order_items || []).forEach((item, key) => {
    const {
      product_id,
      quantity,
      unit_price,
      cancelled_quantity = 0,
      inventory_id,
    } = item;
    const { product_name, product_description, product_unit } =
      product_id as Product;
    const texts = [
      truncateStringSafe(
        `${product_name} ${product_description} ${product_unit}`,
        55,
      ),
      `${quantity - cancelled_quantity}`,
      formatCurrency(unit_price),
      formatCurrency(unit_price * (quantity - cancelled_quantity)),
    ];
    const getXAddend = (key: number, text: string) => {
      switch (key) {
        case 0:
          return 0;
        case 1:
          return 290;
        case 2:
          return width - 230 + (10 - text.length) * 4;
        default:
          return width - 125 + (10 - text.length) * 4;
      }
    };

    texts.forEach((text, key2) => {
      firstPage.drawText(text, {
        x: 42 + getXAddend(key2 as number, text),
        y: height - (283 + 37 * key),
        size: 8,
        font: customFont,
        color: rgb(0, 0, 0),
      });
    });

    firstPage.drawText(
      `Batch No. ${(inventory_id as Inventory)?.batch_number || 'N/A'} Exp. Date: ${dayjs((inventory_id as Inventory)?.expiry_date).format('MM-DD-YYYY')}`,
      {
        x: 42,
        y: height - (301 + 37 * key),
        size: 8,
        font: customFont,
        color: rgb(0, 0, 0),
      },
    );
  });
};

const drawLessVAT = (firstPage: PDFPage, customFont: PDFFont, order: Order) => {
  const { width, height } = firstPage.getSize();
  const { vat_exempted, vat_exempt_amount } = order;
  const text = vat_exempted ? `-${formatCurrency(vat_exempt_amount || 0)}` : '';

  firstPage.drawText(text, {
    x: 45 + (width - 135 + (10 - text.length) * 6),
    y: height - 464 - 20,
    size: 10,
    font: customFont,
    color: rgb(0, 0, 0),
  });
};

const drawTotalSales = (
  firstPage: PDFPage,
  customFont: PDFFont,
  order: Order,
) => {
  const { width, height } = firstPage.getSize();
  const {
    sub_total,
    net_total = 0,
    sc_pwd_discount,
    special_discount,
    sc_pwd_disc_amount,
  } = order;
  const totalSales: string = `${formatCurrency(sub_total || 0)}`;

  const lessSCDisc = sc_pwd_discount
    ? `-${formatCurrency(sc_pwd_disc_amount || 0)}`
    : `-${formatCurrency(special_discount || 0)}`;

  const texts = [totalSales, lessSCDisc, formatCurrency(net_total || 0)];
  texts.forEach((text, key2) =>
    firstPage.drawText(text, {
      x: 42 + (width - 135 + (10 - text.length) * 6),
      y: height - 464 - key2 * 61,
      size: 10,
      font: customFont,
      color: rgb(0, 0, 0),
    }),
  );
};

const drawSCDetails = (
  firstPage: PDFPage,
  customFont: PDFFont,
  order: Order,
) => {
  const { width, height } = firstPage.getSize();
  const { discount_card_number } = order;
  const text: string = `${discount_card_number || ''}`;

  firstPage.drawText(text, {
    x: 42 + (width - 135 + (10 - text.length) * 6),
    y: height - 623,
    size: 10,
    font: customFont,
    color: rgb(0, 0, 0),
  });
};

const drawInitiator = (
  firstPage: PDFPage,
  customFont: PDFFont,
  order: Order,
) => {
  const { height } = firstPage.getSize();
  const { initiator_id } = order;
  // eslint-disable-next-line
  const text: string = `${(initiator_id as any)?.first_name} ${(initiator_id as any)?.last_name}`;

  firstPage.drawText(text, {
    x: 75,
    y: height - 630,
    size: 10,
    font: customFont,
    color: rgb(0, 0, 0),
  });
};

export const modifyPdf = async (
  input: RequestInfo | URL,
  order: Order,
): Promise<void> => {
  try {
    const existingPdfBytes: ArrayBuffer = await fetch(input).then((res) =>
      res.arrayBuffer(),
    );

    const pdfDoc = await PDFDocument.load(existingPdfBytes);

    const fontKitInstance = fontkit;
    pdfDoc.registerFontkit(fontKitInstance as unknown as Fontkit);

    const pages = pdfDoc.getPages();
    const firstPage = pages[0];

    const fontBytes = await fetch('/fonts/DejaVuSans.ttf').then((res) =>
      res.arrayBuffer(),
    );
    const customFont = await pdfDoc.embedFont(fontBytes);

    drawPaymentType(firstPage, customFont, order);
    drawInvoiceNumber(firstPage, customFont, order);
    drawDate(firstPage, customFont, order);
    drawNameTinAddress(firstPage, customFont, order);
    drawOrderItems(firstPage, customFont, order);
    drawTotalSales(firstPage, customFont, order);
    drawLessVAT(firstPage, customFont, order);
    drawSCDetails(firstPage, customFont, order);
    drawInitiator(firstPage, customFont, order);

    const pdfBytes: Uint8Array = await pdfDoc.save();

    const blob = new Blob([pdfBytes], { type: 'application/pdf' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);

    window.open(link.href, '_blank');
  } catch (error) {
    console.error('Error modifying the PDF:', error);
  }
};
